import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { Options } from '@contentful/rich-text-react-renderer'
import { Block as BlockNode, BLOCKS, Document, Inline as InlineNode, INLINES, MARKS } from '@contentful/rich-text-types'
import { css } from '@emotion/core'
import { GatsbyImage } from 'gatsby-plugin-image'
import { ContentfulRichTextGatsbyReference } from 'gatsby-source-contentful/rich-text'
import React from 'react'
import Helmet from 'react-helmet'

import LinkedInPost from '../components/content-management/LinkedInPost'
import RichTextAssetBlock from '../components/content-management/RichTextAssetBlock'
import { DefinitionCard } from '../components/DefinitionCard'
import { Link } from '../components/Link'
import { quoteDividerStyle, TestimonialQuote } from '../components/TestimonialQuote'
import YoutubeVideo from '../components/YoutubeVideo'
import { tikTokBreakPoint } from '../constants/breakPoints'
import { headerHeight } from '../constants/header'
import { useNoFollowLink } from '../hooks/useNoFollowLink'
import QuoteIcon from '../icons/abstract/quote.svg'
import { isMobile } from './common'
import { transformCrossSellingEntryToCtaInline } from './cta'

const getHeadlineId = (text: string) => {
  return (
    'headline-' +
    text
      .toLowerCase()
      .replace(/([^a-z0-9]+)/g, '-')
      .replace(/^\-/, '')
      .replace(/\-$/, '')
  )
}

export const addHeadlineAnchor = (level: number) => (node: BlockNode | InlineNode, children: React.ReactNode) => {
  const text = documentToPlainTextString(node)
  const id = getHeadlineId(text)

  switch (level) {
    case 1:
      return (
        <h1 css={headlineStyle} className="heading1 center">
          <div id={id} css={headlineAnchorStyle} />
          {children}
        </h1>
      )
    case 2:
      return (
        <h2 css={headlineStyle} className="heading2">
          <div id={id} css={headlineAnchorStyle} />
          {children}
        </h2>
      )
    case 3:
      return (
        <h3 css={headlineStyle} className="heading3">
          {children}
        </h3>
      )
    default:
      return <h6 css={headlineStyle}>{children}</h6>
  }
}

export const enhanceContentObject = (
  content: {
    raw: string
    references: ContentfulRichTextGatsbyReference[]
  },
  funnelStage: 'TOFU' | 'MOFU' | 'BOFU',
  categorySlug: string
) => ({
  raw: content.raw,
  references: content.references.map((reference) =>
    reference.__typename === 'ContentfulCrossSellingEntry'
      ? { ...reference, funnelStage, categorySlug }
      : { ...reference }
  ),
})

export const RenderEmbeddedEntry = (node: BlockNode | InlineNode) => {
  const contentTypeId = node.data?.target?.__typename
  switch (contentTypeId) {
    case 'ContentfulDefinitionCard': {
      const target = node.data.target
      const description = target.description.description
      const title = target.title
      return <DefinitionCard title={title} description={description} />
    }
    case 'ContentfulCrossSellingEntry': {
      const target = node.data.target
      const type = target.type
      const resourceSlug = target.ebookResource?.slug
      const funnelStage = target.funnelStage
      const categorySlug = target.categorySlug
      const title = target.title

      // logic to return CTABookADemo / CTAEbook / CTANewsletter / CTAProductWalkthrough / CTATemplates
      return transformCrossSellingEntryToCtaInline(type, resourceSlug, funnelStage, categorySlug, title)
    }
    case 'ContentfulXTwitterTikTokRedditPost': {
      return (
        <RichTextAssetBlock
          withoutPadding={
            !node.data.target.htmlCode.htmlCode.match(/class="tiktok-embed"/) || isMobile(tikTokBreakPoint)
          }
        >
          <Helmet>
            <script async src="https://embed.reddit.com/widgets.js" />
            <script async src="https://www.tiktok.com/embed.js" />
            <script async src="https://platform.twitter.com/widgets.js" />
          </Helmet>
          <div css={tikTokTwitterPostStyle} dangerouslySetInnerHTML={{ __html: node.data.target.htmlCode?.htmlCode }} />
        </RichTextAssetBlock>
      )
    }
    case 'ContentfulChapterSubheading': {
      return (
        <h2 className="heading2 subheading" css={chapterSubheadingStyle} id={node.data.target.contentful_id}>
          {node.data.target.subheading?.subheading}
        </h2>
      )
    }
    case 'ContentfulTestimonialQuote': {
      return (
        <TestimonialQuote
          name={node.data.target.name}
          position={node.data.target.position}
          quote={node.data.target.quote.quote}
          avatarSrc={node.data.target.avatar}
          avatarAlt={node.data.target.name}
        />
      )
    }
    case 'ContentfulImageWithLink': {
      const { link, image, imageDescription, gatsbyImageData } = node.data.target
      if (!image) {
        return
      }
      return (
        <React.Fragment>
          <Link href={link}>
            <GatsbyImage
              {...image}
              image={gatsbyImageData}
              css={gatsbyImageStyle(imageDescription)}
              alt={image.title}
            />
          </Link>
          {imageDescription && (
            <Link href={link}>
              <p className="caption">{imageDescription.description}</p>
            </Link>
          )}
        </React.Fragment>
      )
    }
    default:
      return null
      break
  }
}

export const richTextRenderingOptions: Options = {
  renderMark: {
    [MARKS.BOLD]: (text) => <b>{text}</b>,
    [MARKS.ITALIC]: (text) => <i>{text}</i>,
    [MARKS.UNDERLINE]: (text) => <u>{text}</u>,
    [MARKS.CODE]: (text) => <pre>{text}</pre>,
  },
  renderNode: {
    [INLINES.HYPERLINK]: (node, children) => {
      const uri = node.data.uri
      return (
        <Link
          className="opacity-on-hover"
          href={uri}
          rel={useNoFollowLink(uri) ? 'nofollow' : undefined}
          css={hyperlinkStyle}
        >
          {children}
        </Link>
      )
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) =>
      node.data.target?.gatsbyImageData ? (
        <GatsbyImage
          {...node.data.target}
          image={node.data.target?.gatsbyImageData}
          css={imageStyle}
          alt={node.data.target.title}
        />
      ) : null,
    [BLOCKS.EMBEDDED_ENTRY]: RenderEmbeddedEntry,
    [BLOCKS.HEADING_1]: addHeadlineAnchor(1),
    [BLOCKS.HEADING_2]: addHeadlineAnchor(2),
    [BLOCKS.HEADING_3]: addHeadlineAnchor(3),
    [BLOCKS.QUOTE]: (_node, children) => {
      return (
        <RichTextAssetBlock>
          <QuoteIcon />
          <div className="quote">{children}</div>
          <div css={quoteDividerStyle} />
        </RichTextAssetBlock>
      )
    },
    [BLOCKS.PARAGRAPH]: (node, children) => {
      const text = documentToPlainTextString(node)
      const youtubeMatch = text.match(
        // check both youtube.com/watch?v= and youtube.com/shorts/
        /^https:\/\/www\.youtube.com\/watch\?v=([^&\n]+)$|^https:\/\/www\.youtube.com\/shorts\/([^&\n]+)$/m
      )
      const linkedInMatch = text.match(
        /^<iframe\s+src="https:\/\/www\.linkedin\.com\/embed\/feed\/update\/urn:li:(share|ugcPost):(\d+)"[^>]*>/
      )

      if (youtubeMatch) {
        // if youtubeMatch[1] is undefined, it's a youtube short
        return <YoutubeVideo id={youtubeMatch[1] ?? youtubeMatch[2]} css={youtubeVideoStyle} ratio={16 / 9} />
      } else if (linkedInMatch) {
        return <LinkedInPost embeddedCode={linkedInMatch[0]} />
      } else if (text.startsWith('Caption:')) {
        return <p className="caption">{children}</p>
      } else {
        return <p className="paragraph">{children}</p>
      }
    },
  },
  renderText: (text) => {
    return <span>{text}</span>
  },
}

const chapterSubheadingStyle = css`
  pointer-events: none;
  padding-top: 85px !important;
  margin-top: -85px !important;
`

const gatsbyImageStyle = (imageDescription: string) => css`
  margin: 2rem auto ${imageDescription ? '1rem' : '2rem'};
`

const headlineStyle = css`
  position: relative;
`

const headlineAnchorStyle = css`
  position: absolute;
  left: 0;
  top: -${Math.max(headerHeight + 13, 0)}px;
`

const hyperlinkStyle = css`
  word-break: break-word;
`

const imageStyle = css`
  margin: 2rem auto;
`

const tikTokTwitterPostStyle = css`
  .twitter-tweet,
  .tiktok-embed {
    margin: 0 auto !important;

    @media screen and (min-width: 385px) and (max-width: ${tikTokBreakPoint}px) {
      padding-top: 3vw !important;
      padding-bottom: 3vw !important;
      background-color: #fff;
      box-shadow: 2px 2px 20px rgba(69, 118, 255, 0.3);
    }
  }
`

const youtubeVideoStyle = css`
  margin-bottom: 1.5rem;
`

export const getAnchors = (document: Document) => {
  const anchors = document.content
    .filter((node) => node.nodeType === 'heading-2' || node.nodeType === 'heading-1')
    .map((node) => {
      const text = documentToPlainTextString(node)
      return { label: text, id: getHeadlineId(text) }
    })

  return anchors
}
